import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

export interface WeatherDailyData {
  date: string;
  total: number;
  arrived: number;
  cancelled: number;
  planned: number;
  people: number;
  weatherCode: number | null;
  weatherCategory: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'unknown';
  weatherLabel: string;
  tempMax: number | null;
  tempMin: number | null;
  precipitation: number | null;
  sunshine: number | null;
}

export interface WeatherTypeData {
  key: string;
  category: string;
  icon: string;
  totalReservations: number;
  avgReservations: number;
  avgPeople: number;
  arrivalRate: number;
  cancellationRate: number;
  daysCount: number;
}

export interface TemperatureRangeData {
  range: string;
  totalReservations: number;
  avgReservations: number;
  arrivalRate: number;
  daysCount: number;
}

export interface WeatherInsights {
  bestWeatherType: string;
  bestWeatherAvg: number;
  worstWeatherType: string;
  worstWeatherAvg: number;
  rainImpact: number;
  optimalTemperature: string | null;
}

export interface WeatherStatisticsData {
  dailyData: WeatherDailyData[];
  byWeatherType: WeatherTypeData[];
  byTemperature: TemperatureRangeData[];
  insights: WeatherInsights | null;
  hasData: boolean;
  hasWeatherData: boolean;
  weatherDataNote: string | null;
  coordinates?: { latitude: number; longitude: number };
}

interface UseWeatherStatisticsParams {
  startDate: Date;
  endDate: Date;
}

export function useWeatherStatistics({ startDate, endDate }: UseWeatherStatisticsParams) {
  const [data, setData] = useState<WeatherStatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Non authentifié");
      }

      const formattedStart = format(startDate, 'yyyy-MM-dd');
      const formattedEnd = format(endDate, 'yyyy-MM-dd');

      const { data: responseData, error: invokeError } = await supabase.functions.invoke('get-weather-statistics', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        },
        body: {
          startDate: formattedStart,
          endDate: formattedEnd
        }
      });

      if (invokeError) {
        throw new Error(invokeError.message || 'Erreur serveur');
      }

      setData(responseData);
    } catch (err: any) {
      console.error("Error fetching weather statistics:", err);
      setError(err.message || "Erreur lors du chargement des statistiques météo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate.toISOString(), endDate.toISOString()]);

  return { data, loading, error, refetch: fetchData };
}
